// Central HTTP client. Sends cookies (`credentials: 'include'`) on every
// request and runs a 401 → /auth/refresh → retry-once interceptor. Callers
// can pass `opts.headers` to include service-specific headers (e.g.
// X-API-Key when targeting a localhost service the cookie can't reach).
//
// Throws `UnauthorizedError` after a failed refresh or on 401 from the
// no-refresh paths; throws `ApiError` for other non-OK responses.

import { ApiError, UnauthorizedError } from './errors.js'
import { setUploadSessionRefresh } from './upload.js'

// Refresh-retry must NOT recurse on these paths.
// /auth/refresh: the recovery channel itself.
// /auth/verify: probe used on app load — caller wants direct 401 signal.
// /auth/login: failed login is not recoverable by refresh.
const NO_REFRESH_PATHS = new Set(['/auth/refresh', '/auth/verify', '/auth/login'])

// Transient network-error retry (fetch throws, before any HTTP response).
const NET_RETRIES = 2
const NET_BACKOFF_MS = 300

function sleep(ms) { return new Promise(r => setTimeout(r, ms)) }

function baseUrl() {
  return import.meta.env?.VITE_AUTH_SERVICE_URL ?? ''
}

function buildUrl(path, query) {
  // Absolute URLs (used for direct-to-AAM JSON calls when VITE_ASSET_MANAGER_URL
  // is the AAM host) pass through unchanged.
  //
  // `/auth/*` paths are qualified with VITE_AUTH_SERVICE_URL because the
  // auth-service is always cross-origin (separate subdomain in dev and prod).
  //
  // Every other relative path stays relative. `/api/*` is Convorcher's
  // namespace and `/aam/*` is the asset-manager dev-proxy alias — both are
  // resolved against the SPA's own origin and routed by Vite's dev proxy
  // (or nginx in deploy). Qualifying these with the auth-service URL would
  // misroute them to the wrong host AND fail CORS preflight (auth-service's
  // CORS only allows Content-Type; AAM-targeted requests carry X-API-Key,
  // which auth-service rejects). That's the bug the URL-routing rule below
  // is here to prevent.
  const isAbsolute = /^https?:\/\//i.test(path)
  let url
  if (isAbsolute) {
    url = path
  } else if (path.startsWith('/auth/')) {
    url = `${baseUrl()}${path}`
  } else {
    url = path
  }
  if (query) {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) params.set(k, String(v))
    }
    const qs = params.toString()
    if (qs) url += (url.includes('?') ? '&' : '?') + qs
  }
  return url
}

function buildInit(opts) {
  const init = {
    method: opts.method ?? 'GET',
    credentials: 'include',
  }
  if (opts.signal) init.signal = opts.signal
  const headers = { ...(opts.headers ?? {}) }
  if (opts.body !== undefined && !(opts.body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
    init.body = typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body)
  } else if (opts.body !== undefined) {
    init.body = opts.body
  }
  if (Object.keys(headers).length > 0) init.headers = headers
  return init
}

async function readErrorBody(res) {
  try {
    return await res.json()
  } catch {
    return null
  }
}

// The status comes from the RESPONSE, never from the error body. A middleware
// rejection (e.g. a CORS 403 from gin-cors) carries an empty body — reading
// `body?.status` there rendered the undiagnosable "HTTP unknown" in the
// character-delete error dialog (ENG-1025), when "HTTP 403" names the problem.
function readErrorMessage(res, body) {
  if (body && typeof body.error === 'string') return body.error
  return `HTTP ${String(res.status)}`
}

function elapsedMs(t0) {
  return Math.round(performance.now() - t0)
}

// Path key for NO_REFRESH_PATHS — strip query + base.
function refreshKey(path) {
  const stripped = path.split('?')[0]
  if (/^https?:\/\//i.test(stripped)) {
    try { return new URL(stripped).pathname } catch { return stripped }
  }
  return stripped
}

/**
 * Ask the auth-service for a fresh session cookie. Returns whether it worked.
 *
 * EXPORTED because `apiFetch` is not the only transport that talks to an
 * authenticated backend: file uploads that need a progress bar go through raw
 * XHR (`upload.js` — fetch cannot emit upload progress on a JS body in Chrome),
 * and that path had no refresh at all. So an expired access token was INVISIBLE
 * on every read — `apiFetch` refreshed and retried silently — and a hard
 * `401 {"error":"unauthorized"}` on any upload, which reads to the user as "the
 * uploader is broken" rather than "your session lapsed".
 *
 * It lives here, and is not re-implemented over there, because the auth-service
 * is cross-origin and only `buildUrl` knows how to reach it — a second copy of
 * that URL rule is the drift this module exists to prevent.
 */
export async function refreshSession() {
  const t0 = performance.now()
  const res = await fetch(buildUrl('/auth/refresh'), { method: 'POST', credentials: 'include' })
  console.log(`[api] POST /auth/refresh ${String(res.status)} (${String(elapsedMs(t0))}ms)`)
  return res.ok
}

// Hand the XHR upload transport the same refresh this module runs, so a lapsed
// token recovers identically on both. Registered here rather than imported
// there: `upload.js` must stay loadable under plain `node --test`.
setUploadSessionRefresh(refreshSession)

export async function apiFetch(path, opts = {}) {
  const url = buildUrl(path, opts.query)
  const init = buildInit(opts)
  const method = init.method ?? 'GET'

  // Transient network errors — a CI agent's interface flapping
  // (net::ERR_NETWORK_CHANGED) or a brief connectivity drop — surface as
  // fetch() THROWING (a TypeError), distinct from any HTTP response. A single
  // blip must not fail a whole avatar boot: both __AVATAR_READY__ (AvatarModel
  // listAnimationAssets) and __FACE_CLIP_PLAYER__._loaded (FaceClipPlayer
  // loadAll → listGestures) gate on these list calls, and one dropped request
  // used to abort the entire load. Retry the network-throw a few times with
  // backoff. HTTP responses (incl. 4xx/5xx) are NOT retried here — they flow to
  // the status handling below, preserving the loud-failure contract. An aborted
  // request (opts.signal) is intentional, not transient, so it never retries.
  const t0 = performance.now()
  let res
  for (let attempt = 0; ; attempt++) {
    try {
      res = await fetch(url, init)
      break
    } catch (err) {
      const aborted = err?.name === 'AbortError' || init.signal?.aborted
      if (aborted || attempt >= NET_RETRIES) {
        console.log(`[api] ${method} ${path} network-error (${String(elapsedMs(t0))}ms)`)
        throw err
      }
      console.log(`[api] ${method} ${path} network-error — retry ${String(attempt + 1)}/${String(NET_RETRIES)}`)
      await sleep(NET_BACKOFF_MS * (attempt + 1))
    }
  }
  console.log(`[api] ${method} ${path} ${String(res.status)} (${String(elapsedMs(t0))}ms)`)

  if (res.status === 401 && !NO_REFRESH_PATHS.has(refreshKey(path))) {
    const ok = await refreshSession()
    if (!ok) throw new UnauthorizedError()
    const retryT0 = performance.now()
    res = await fetch(url, init)
    console.log(
      `[api] ${method} ${path} ${String(res.status)} (${String(elapsedMs(retryT0))}ms) [retry]`,
    )
  }

  if (res.status === 401) throw new UnauthorizedError()
  if (!res.ok) {
    const body = await readErrorBody(res)
    const message = readErrorMessage(res, body)
    const err = new ApiError(res.status, message)
    // For 409 version conflicts, attach the full response body so the caller
    // can extract the current row from the error.current field.
    if (res.status === 409 && body) {
      err.error = body.error
      err.current = body.current
    }
    throw err
  }
  if (res.status === 204) return undefined
  // Default to JSON. Some endpoints return non-JSON bodies (e.g. convorcher
  // story export returns YAML text) — those callers pass responseType so we
  // read the body correctly instead of throwing in res.json().
  const responseType = opts.responseType ?? 'json'
  if (responseType === 'text') return res.text()
  if (responseType === 'blob') return res.blob()
  return res.json()
}
